import { Lot } from './types';

/**
 * Lot-specific utility functions.
 * For formatting utilities (currency, percentages, HTML escaping), see formatting.ts
 */

export function normalizeHomeZipPostal(input: string | null): string {
  try {
    const raw = (input == null ? '' : String(input)).trim().toUpperCase();
    if (!raw) return '';

    const dashNorm = raw.replace(/[‐-―−﹘﹣－]/g, '-').replace(/[​-‍⁠ ]/g, ' ');

    const caCompact = dashNorm.replace(/\s+/g, '');
    if (/^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/.test(caCompact)) {
      return caCompact;
    }

    const digits = dashNorm.replace(/[^0-9]/g, '');
    if (digits.length >= 5) {
      return digits.substring(0, 5);
    }

    if (/^[A-Z][0-9][A-Z][ ]?[0-9][A-Z][0-9]$/.test(dashNorm)) {
      return dashNorm.replace(/\s+/g, '');
    }

    return '';
  } catch {
    return '';
  }
}

export function formatCountdown(closeTimestamp: number): { text: string; isUrgent: boolean } {
  const now = Date.now();
  const msRemaining = closeTimestamp - now;

  if (msRemaining <= 0) {
    return { text: 'Closed', isUrgent: false };
  }

  const seconds = Math.floor(msRemaining / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let text = '';
  let isUrgent = false;

  if (days > 0) {
    text = `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    text = `${hours}h ${minutes % 60}m`;
    isUrgent = hours < 1;
  } else if (minutes > 0) {
    text = `${minutes}m`;
    isUrgent = true;
  } else {
    text = `${seconds}s`;
    isUrgent = true;
  }

  return { text, isUrgent };
}

export function getCloseTimestamp(
  lot: Lot,
  getEffectiveCloseTimeFn: (lot: Lot) => number | null
): number | undefined {
  if (!lot) return undefined;
  const closeAt = getEffectiveCloseTimeFn(lot);
  if (typeof closeAt === 'number' && Number.isFinite(closeAt)) return closeAt;
  if (typeof closeAt === 'string') {
    const parsed = Date.parse(closeAt);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

export function isLotClosed(
  lot: Lot,
  getEffectiveCloseTimeFn: (lot: Lot) => number | null
): boolean {
  const raw = (lot as any)?.sharedLot?.payload?.raw;
  const backendClosed = raw?.lotlingo?.closed;
  if (backendClosed === true) return true;
  if (backendClosed === false) return false;
  const closeTs = getCloseTimestamp(lot, getEffectiveCloseTimeFn);
  return typeof closeTs === 'number' && Number.isFinite(closeTs) && closeTs <= Date.now();
}

export function getUpdatedTimestamp(lot: Lot): number {
  if (!lot) return 0;
  const updated = typeof lot.updatedAt === 'number' ? lot.updatedAt : Number(lot.updatedAt);
  if (Number.isFinite(updated)) {
    return updated;
  }
  const created = typeof lot.createdAt === 'number' ? lot.createdAt : Number(lot.createdAt);
  if (Number.isFinite(created)) {
    return created;
  }
  return 0;
}

export function stripCacheBustParam(url: string): string {
  if (typeof url !== 'string' || url.indexOf('__bust=') === -1) {
    return url;
  }
  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.delete('__bust');
    return parsed.toString();
  } catch {
    let cleaned = url.replace(/([?&])__bust=[^&]*(?:&)?/g, (match, prefix) => {
      if (prefix === '?') {
        return match.charAt(match.length - 1) === '&' ? '?' : '';
      }
      return '';
    });
    if (cleaned.endsWith('?') || cleaned.endsWith('&')) {
      cleaned = cleaned.slice(0, -1);
    }
    return cleaned;
  }
}

export function formatPriceStatusValue(lot: Lot): string {
  if (!lot || !lot.priceStatus) return '';
  return String(lot.priceStatus).trim();
}

export function hasPricingData(lot: Lot): boolean {
  if (!lot) return false;
  return !!(
    lot.lowSellValue != null ||
    lot.highSellValue != null ||
    lot.lowSellValueRaw ||
    lot.highSellValueRaw ||
    (Array.isArray(lot.lowListings) && lot.lowListings.length > 0) ||
    (Array.isArray(lot.highListings) && lot.highListings.length > 0) ||
    lot.priceStatus ||
    lot.aiAnalysis
  );
}

// Re-export skeleton generators for backward compatibility
export { estimateSkeleton } from './skeletons';

// Re-export deepClone from shared utilities for backward compatibility
export { deepClone } from '../../shared/utils.js';
